/**
 * A company bonus line carries `payed` and `payed_type`: the same number is a
 * percentage of the mission's hourly rate or an amount in euros per unit, and
 * only `payed_type` says which. Every screen showing the value goes through
 * here, so a 5 % never reads as 5 € on one of them.
 */
export const BONUS_VALUE_TYPES = {
  PERCENTAGE: 'percentage',
  AMOUNT: 'amount',
};

const DASH = '—';

/**
 * The type a line falls back to when it has none stored — the rule the api
 * applies to lines saved before the type existed. A rubric whose coefficient
 * is strictly between 0 and 1 is a majoration (0.3 for +30 %); any other is
 * paid as an amount.
 */
export const bonusValueTypeFromCoefficient = (coefficient) =>
  coefficient > 0 && coefficient < 1
    ? BONUS_VALUE_TYPES.PERCENTAGE
    : BONUS_VALUE_TYPES.AMOUNT;

const percentageFormat = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 2,
});
const amountFormat = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * « 5 % » or « 12,00 € ». A line with no known type shows its bare number
 * rather than guessing a unit; a line with no value shows `fallback`.
 */
export const formatBonusValue = (bonus, fallback = DASH) => {
  const payed = bonus?.payed;
  if (payed === undefined || payed === null || payed === '') return fallback;

  const number = Number(payed);
  if (!Number.isFinite(number)) return fallback;

  if (bonus.payed_type === BONUS_VALUE_TYPES.PERCENTAGE) {
    return `${percentageFormat.format(number)} %`;
  }
  if (bonus.payed_type === BONUS_VALUE_TYPES.AMOUNT) {
    return `${amountFormat.format(number)} €`;
  }
  return String(payed);
};

export default formatBonusValue;
